import React, { Suspense } from 'react';
import { Routes, Route, useLocation, Navigate, RouteObject } from 'react-router-dom';
import { BaseLayout } from '../shared/components/layout/BaseLayout';
import { PageLoader } from '../shared/components/ui/PageLoader';
import { useLeadsContext } from '../shared/contexts/LeadsContext';
import { useClientsContext } from '../shared/contexts/ClientsContext';
import { useAuth } from '../shared/contexts/AuthContext';
import { LeadsPageProvider } from '../contexts/LeadsPageContext';
import { RouteGuard } from './RouteGuard';
import { routes } from './index';
import { EmailDeliverabilityBackgroundIndicator } from '../modules/integrations/components/EmailDeliverabilityBackgroundIndicator';

/**
 * Helper function to render routes recursively with their children
 */
const renderRoutes = (routes: RouteObject[]): React.ReactNode => {
  return routes.map((route, index) => {
    if (route.children && route.children.length > 0) {
      // Route with children - render nested routes
      return (
        <Route
          key={route.path || index}
          path={route.path}
          element={route.element}
        >
          {renderRoutes(route.children as RouteObject[])}
        </Route>
      );
    }

    // Leaf route - no children
    return (
      <Route
        key={route.path || index}
        path={route.path}
        element={route.element}
        index={route.index}
      />
    );
  });
};

/**
 * AppRouter - Router principal de la aplicación con React Router v6
 *
 * Características:
 * - Lazy loading de rutas
 * - Suspense para carga de páginas
 * - Layout unificado (BaseLayout)
 * - Deep linking habilitado
 * - Datos compartidos vía LeadsContext y ClientsContext
 */

let routerRenderCount = 0;

function AppRouterContent() {
  const location = useLocation();
  const { leadCount } = useLeadsContext();
  const { clientCount } = useClientsContext();
  const { isAuthenticated, isLoading } = useAuth();

  // Debug logs disabled for production
  // routerRenderCount++;
  // console.log('🔷 [NAV] AppRouterContent rendering #' + routerRenderCount + ', location:', location.pathname);

  // Check if this is a public route that doesn't require authentication
  const isPublicRoute =
    location.pathname === '/login' ||
    location.pathname === '/login-v1' ||
    location.pathname === '/login-v2' ||
    location.pathname === '/login-v3' ||
    location.pathname.startsWith('/public/') ||
    location.pathname.startsWith('/quotes/public/') ||
    location.pathname.startsWith('/orbit-view/') ||
    location.pathname.startsWith('/showcase/') ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/change-password' ||
    location.pathname.startsWith('/portal/') ||  // Portal de clientes legacy
    location.pathname.startsWith('/portal-clientes/') ||  // Portal de clientes multi-tenant
    location.pathname.startsWith('/scoring/') ||  // Scoring IA voice agent (single-use token)
    location.pathname.startsWith('/setup/email-domain/') ||  // Vista del tecnico para delegacion de subdominio (token)
    location.pathname.startsWith('/tools');  // SEO tools (public)

  // Para rutas públicas, NO mostrar loader de autenticación
  // Esto evita el doble spinner (auth loading + data loading)
  // Las rutas públicas manejan su propio loading state
  if (isLoading && !isPublicRoute) {
    return <PageLoader />;
  }

  // Django admin is accessible directly at http://localhost:8000/admin/
  // All /admin/* routes in the React app are for the admin role dashboard

  // Si no está autenticado, redirigir a login (excepto para rutas públicas)
  // Guard: si hay tokens almacenados AND auth is still loading, Firebase may still be initializing — wait.
  // But if auth finished loading (isLoading=false) and we're still not authenticated,
  // the stored tokens are stale — clear them and redirect to login.
  if (!isAuthenticated && !isPublicRoute) {
    const hasStoredSession =
      !!localStorage.getItem('firebase_token') ||
      !!localStorage.getItem('owly_user') ||
      !!localStorage.getItem('owly_access_token');
    if (hasStoredSession && isLoading) {
      // Auth still initializing, wait
      return <PageLoader />;
    }
    if (hasStoredSession && !isLoading) {
      // Auth finished but session is invalid — clear stale tokens
      localStorage.removeItem('firebase_token');
      localStorage.removeItem('owly_user');
      localStorage.removeItem('owly_access_token');
      localStorage.removeItem('user');
    }
    return <Navigate to="/login" replace />;
  }

  // Full-screen routes that bypass BaseLayout (sidebar hidden)
  const isFullScreenRoute = location.pathname === '/leads/score-3d'
    || /^\/quotes\/[^/]+\/edit$/.test(location.pathname);

  if (isFullScreenRoute) {
    return (
      <RouteGuard>
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
            {renderRoutes(routes)}
          </Routes>
        </Suspense>
      </RouteGuard>
    );
  }

  // For public routes, render without BaseLayout
  // OrbitView necesita h-screen para ocupar pantalla completa
  // QuotePublicPage usa min-h-screen para permitir scroll
  const isOrbitViewRoute =
    location.pathname.includes('/orbit-view/') ||
    location.pathname.includes('/showcase/') ||
    location.pathname.startsWith('/scoring/');

  const isToolsRoute = location.pathname.startsWith('/tools');

  if (isPublicRoute) {
    // Tools pages manage their own background — render without glassmorphism wrapper
    if (isToolsRoute) {
      return (
        <div className="w-full h-screen overflow-y-auto">
          <Suspense fallback={<PageLoader />}>
            <Routes location={location}>
              {renderRoutes(routes)}
            </Routes>
          </Suspense>
        </div>
      );
    }

    // Quote and OrbitView pages have their own BrandedBackground that reads theme CSS variables.
    // Other public routes (login, portal, etc.) use the default hardcoded gradient.
    const hasOwnBackground =
      location.pathname.startsWith('/quotes/public/') ||
      location.pathname.startsWith('/orbit-view/') ||
      location.pathname.startsWith('/showcase/') ||
      location.pathname.startsWith('/scoring/');

    return (
      <div className={`w-full relative ${isOrbitViewRoute ? 'h-full overflow-hidden' : 'h-screen overflow-y-auto'}`}>
        {/* Fondo con gradiente mesh orgánico — solo para rutas que NO tienen BrandedBackground propio */}
        {!hasOwnBackground && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-orange-50/30 to-blue-100/50 dark:from-[#2D2D2D] dark:via-[#2F2F2F] dark:to-[#2D2D2D]">
            <div className="absolute inset-0 dark:hidden" style={{
              backgroundImage: `
                radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
                radial-gradient(at 100% 0%, rgba(251, 146, 60, 0.12) 0px, transparent 50%),
                radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.1) 0px, transparent 50%),
                radial-gradient(at 0% 100%, rgba(251, 146, 60, 0.15) 0px, transparent 50%),
                radial-gradient(at 50% 50%, rgba(147, 197, 253, 0.08) 0px, transparent 50%)
              `
            }}></div>
            <div className="absolute inset-0 hidden dark:block" style={{
              backgroundImage: `
                radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.06) 0px, transparent 50%),
                radial-gradient(at 100% 0%, rgba(251, 146, 60, 0.04) 0px, transparent 50%),
                radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.05) 0px, transparent 50%),
                radial-gradient(at 0% 100%, rgba(251, 146, 60, 0.06) 0px, transparent 50%),
                radial-gradient(at 50% 50%, rgba(100, 100, 100, 0.04) 0px, transparent 50%)
              `
            }}></div>
          </div>
        )}

        {/* Contenido encima del fondo - debe ocupar toda la altura */}
        <div className={`relative z-10 ${isOrbitViewRoute ? 'h-full' : ''}`}>
          <Routes location={location}>
            {renderRoutes(routes)}
          </Routes>
        </div>
      </div>
    );
  }

  // Log para debug cuando está autenticado

  // Determinar página actual basado en pathname
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path.startsWith('/leads')) return 'leads';
    if (path.startsWith('/clients')) return 'clients';
    if (path.startsWith('/chat')) return 'omnichannel';
    if (path.startsWith('/calendar')) return 'calendar';
    if (path.startsWith('/omnichannel')) return 'omnichannel';
    if (path.startsWith('/ai-center')) return 'ai-center';
    if (path.startsWith('/buyer-persona')) return 'buyer-persona';
    if (path.startsWith('/project-structuring')) return 'project-structuring';
    if (path.startsWith('/pipeline-versions')) return 'pipeline-versions';
    if (path.startsWith('/pipeline-config')) return 'pipeline-config';
    if (path.startsWith('/team')) return 'team';
    if (path.startsWith('/docs')) return 'docs';
    if (path.startsWith('/test')) return 'test';
    return 'dashboard';
  };

  // Determinar modo (leads/clients) basado en ruta
  const getAppMode = (): 'leads' | 'clients' => {
    return location.pathname.startsWith('/clients') ? 'clients' : 'leads';
  };

  const isLeadsRoute = location.pathname.startsWith('/leads');

  const currentPage = getCurrentPage();

  const layoutContent = (
    <RouteGuard>
      {/* Panel flotante abajo a la derecha mostrando identidades de Email
          Deliverability "en segundo plano" (patron tipo BackgroundUploadNotification
          de orbit). Persiste cross-app — se ve desde cualquier ruta. */}
      <EmailDeliverabilityBackgroundIndicator />
      <BaseLayout
        currentPage={currentPage as any}
        mode={getAppMode()}
        onModeChange={() => {}}
        leadCount={leadCount}
        clientCount={clientCount}
        activeChatTab="conversation"
        onChatTabChange={() => {}}
        activeFocusMode="all"
        onFocusModeChange={() => {}}
        activeLeadTab="profile"
        onLeadTabChange={() => {}}
        currentLead={null}
      >
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
            {renderRoutes(routes)}
          </Routes>
        </Suspense>
      </BaseLayout>
    </RouteGuard>
  );

  return isLeadsRoute ? (
    <LeadsPageProvider>{layoutContent}</LeadsPageProvider>
  ) : (
    layoutContent
  );
}

export function AppRouter() {
  return <AppRouterContent />;
}
